import { ELKLayoutConfig, NodePosition, ViewportBounds, ElkNode, ElkExtendedEdge } from './types'
import { JourneyNode, JourneyEdge } from '../types/journey-nodes'
import { LAYOUT_CONSTANTS } from './layout-config'
import ELK from 'elkjs/lib/elk.bundled.js'

const elk = new ELK()

/**
 * Convert Journey nodes and edges to ELK graph format
 * 
 * This function transforms the Journey Builder's node/edge representation into
 * the format expected by the ELK layout engine. It maps:
 * - JourneyNode dimensions to ELK node format
 * - JourneyEdge connections to ELK edge format
 * - Layout configuration to ELK layout options
 * 
 * If node dimensions are not available, uses default dimensions from LAYOUT_CONSTANTS
 * and logs a warning for debugging.
 * 
 * @example
 * ```typescript
 * const elkGraph = convertToELKGraph(
 *   journeyNodes,
 *   journeyEdges,
 *   DEFAULT_ELK_CONFIG
 * )
 * // Returns ELK graph with layoutOptions configured for orthogonal routing
 * ```
 * 
 * @param nodes - Array of Journey nodes with dimensions
 * @param edges - Array of Journey edges connecting nodes
 * @param config - ELK layout configuration options
 * @returns ELK graph structure ready for layout calculation
 */
export function convertToELKGraph(
    nodes: JourneyNode[],
    edges: JourneyEdge[],
    config: ELKLayoutConfig,
): ElkNode {
    return {
        id: 'journey-root',
        // Configure ELK layout options using string key-value pairs
        // ELK expects all options as strings, even numeric values
        layoutOptions: {
            'elk.algorithm': config.algorithm,
            'elk.direction': config.direction,
            'elk.edgeRouting': config.edgeRouting,
            'elk.spacing.nodeNode': `${config.nodeSeparation}`,
            'elk.layered.spacing.nodeNodeBetweenLayers': `${config.rankSeparation}`,
            // ELK padding format: [top=X,left=X,bottom=X,right=X]
            'elk.padding': `[top=${config.padding},left=${config.padding},bottom=${config.padding},right=${config.padding}]`,
            // Center-align nodes horizontally for straight vertical edges
            'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
            'elk.alignment': 'CENTER',
        },
        // Convert Journey nodes to ELK node format
        // ELK needs width/height to calculate layout, positions are computed by ELK
        children: nodes.map(node => {
            const width = node.measured?.width ?? LAYOUT_CONSTANTS.MIN_NODE_WIDTH
            const height = node.measured?.height ?? LAYOUT_CONSTANTS.MIN_NODE_HEIGHT
            
            // Log warning if using default dimensions (dev mode only)
            // This helps debug layout issues when nodes haven't been measured yet
            if ((!node.measured?.width || !node.measured?.height) && process.env.NODE_ENV === 'development') {
                console.warn('Node dimensions not available, using defaults:', {
                    nodeId: node.id,
                    nodeType: node.type,
                    usingDefaultWidth: !node.measured?.width,
                    usingDefaultHeight: !node.measured?.height,
                    defaultWidth: LAYOUT_CONSTANTS.MIN_NODE_WIDTH,
                    defaultHeight: LAYOUT_CONSTANTS.MIN_NODE_HEIGHT,
                })
            }
            
            return {
                id: node.id,
                width,
                height,
            }
        }),
        // Convert Journey edges to ELK edge format
        // ELK uses sources/targets arrays to support hyperedges (multiple sources/targets)
        // For journey workflows, we always have single source and target
        edges: edges.map(edge => ({
            id: edge.id,
            sources: [edge.source],
            targets: [edge.target],
        })) as ElkExtendedEdge[],
    }
}

/**
 * Execute ELK layout and return positioned nodes
 * 
 * This function:
 * 1. Converts Journey nodes/edges to ELK format
 * 2. Executes the ELK layout algorithm asynchronously
 * 3. Extracts computed positions from the result
 * 
 * Performance monitoring is included in development mode to track
 * layout calculation times. Warnings are logged if calculation
 * exceeds performance targets:
 * - ≤20 nodes: 100ms target
 * - ≤50 nodes: 500ms target
 * 
 * @example
 * ```typescript
 * const positions = await calculateELKLayout(
 *   journeyNodes,
 *   journeyEdges,
 *   DEFAULT_ELK_CONFIG
 * )
 * // Returns Map<string, NodePosition> with computed coordinates
 * ```
 * 
 * @param nodes - Array of Journey nodes
 * @param edges - Array of Journey edges
 * @param config - ELK layout configuration
 * @returns Promise resolving to Map of node IDs to computed positions
 */
export async function calculateELKLayout(
    nodes: JourneyNode[],
    edges: JourneyEdge[],
    config: ELKLayoutConfig,
): Promise<Map<string, NodePosition>> {
    // Start performance measurement to track layout calculation time
    const startTime = performance.now()
    
    // Step 1: Convert Journey format to ELK format
    const elkGraph = convertToELKGraph(nodes, edges, config)
    
    // Step 2: Execute ELK layout algorithm asynchronously
    // ELK runs in a web worker to avoid blocking the UI thread
    const layoutedGraph = await elk.layout(elkGraph)
    
    // Step 3: Extract computed positions from ELK result
    // ELK adds x/y coordinates to each node in the layouted graph
    const positions = new Map<string, NodePosition>()
    for (const node of layoutedGraph.children ?? []) {
        if (node.x !== undefined && node.y !== undefined) {
            positions.set(node.id, { x: node.x, y: node.y })
        }
    }
    
    // End performance measurement and log metrics
    const endTime = performance.now()
    const calculationTime = endTime - startTime
    
    // Log performance metrics to development console (dev mode only)
    if (process.env.NODE_ENV === 'development') {
        console.debug('ELK Layout Performance:', {
            nodeCount: nodes.length,
            edgeCount: edges.length,
            calculationTime: `${calculationTime.toFixed(2)}ms`,
            timePerNode: `${(calculationTime / nodes.length).toFixed(2)}ms`,
        })
        
        // Warn if calculation time exceeds performance targets
        if (nodes.length <= 20 && calculationTime > 100) {
            console.warn('ELK layout exceeded target time for small workflow:', {
                nodeCount: nodes.length,
                calculationTime: `${calculationTime.toFixed(2)}ms`,
                target: '100ms',
            })
        } else if (nodes.length <= 50 && calculationTime > 500) {
            console.warn('ELK layout exceeded target time for medium workflow:', {
                nodeCount: nodes.length,
                calculationTime: `${calculationTime.toFixed(2)}ms`,
                target: '500ms',
            })
        }
    }
    
    return positions
}

/**
 * Apply ELK-computed positions to Journey nodes
 * 
 * Takes the positions calculated by ELK and applies them to the Journey nodes,
 * creating new node objects with updated positions while preserving all other
 * node properties.
 * 
 * The Y positions are normalized so that the topmost node starts at Y=0,
 * ensuring consistent positioning regardless of ELK's internal padding.
 * 
 * @param nodes - Array of Journey nodes
 * @param positions - Map of node IDs to computed positions
 * @returns Array of Journey nodes with updated positions normalized to start at top
 */
export function applyELKLayout(
    nodes: JourneyNode[],
    positions: Map<string, NodePosition>,
): JourneyNode[] {
    // Find minimum Y coordinate to normalize positions to top
    let minY = Infinity
    positions.forEach(pos => {
        minY = Math.min(minY, pos.y)
    })
    
    // If no positions found, minY will be Infinity, so default to 0
    const yOffset = minY === Infinity ? 0 : minY
    
    return nodes.map(node => {
        const position = positions.get(node.id)
        if (!position) return node
        
        return {
            ...node,
            position: {
                x: position.x,
                y: position.y - yOffset,
            },
        }
    })
}

/**
 * Extract edge routing information from ELK layout
 * 
 * Used for custom edge rendering if needed. Returns the routing sections
 * computed by ELK for a specific edge, which includes start/end points
 * and optional bend points for orthogonal routing.
 * 
 * This is useful when implementing custom edge components that need to
 * follow ELK's computed routing paths instead of using React Flow's
 * default edge rendering.
 * 
 * @example
 * ```typescript
 * const routing = getELKEdgeRouting(layoutedGraph, 'edge-1')
 * if (routing) {
 *   // Use routing.sections to render custom edge path
 *   const { startPoint, endPoint, bendPoints } = routing[0]
 * }
 * ```
 * 
 * @param layoutedGraph - ELK graph after layout calculation
 * @param edgeId - ID of the edge to get routing for
 * @returns Edge routing sections or undefined if not found
 */
export function getELKEdgeRouting(
    layoutedGraph: ElkNode,
    edgeId: string,
) {
    const edge = layoutedGraph.edges?.find(e => e.id === edgeId)
    return edge?.sections
}

/**
 * Calculate viewport bounds from positioned nodes
 *
 * Computes the bounding box that encompasses all nodes in the workflow,
 * with additional padding around the content. This is used to:
 * - Determine the canvas size needed to display the workflow
 * - Center the workflow content in the viewport
 * - Enable proper scrolling when content exceeds viewport
 *
 * The viewport dimensions are clamped to minimum values to ensure
 * the layout remains usable on small screens.
 *
 * @example
 * ```typescript
 * const bounds = calculateViewportBounds(positionedNodes, 40)
 * // Returns { width: 1200, height: 800, minX: -40, maxX: 1160, minY: -40, maxY: 760 }
 * // Use bounds.width and bounds.height to size the React Flow container
 * ```
 *
 * @param nodes - Array of Journey nodes with computed positions
 * @param padding - Padding to add around the workflow content (default: 40px)
 * @returns ViewportBounds with dimensions and coordinate boundaries
 */
export function calculateViewportBounds(
    nodes: JourneyNode[],
    padding: number = 40,
): ViewportBounds {
    // Handle empty workflow case
    // Return minimum viewport size with no content bounds
    if (nodes.length === 0) {
        return {
            width: Math.max(padding * 2, LAYOUT_CONSTANTS.MIN_VIEWPORT_WIDTH),
            height: Math.max(padding * 2, LAYOUT_CONSTANTS.MIN_VIEWPORT_HEIGHT),
            minX: 0,
            maxX: 0,
            minY: 0,
            maxY: 0,
        }
    }

    // Initialize bounds with extreme values
    // These will be updated as we iterate through nodes
    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity

    // Calculate min/max coordinates from all nodes
    // We need to consider both node position and dimensions
    for (const node of nodes) {
        const x = node.position.x
        const y = node.position.y
        const width = node.measured?.width ?? 200
        const height = node.measured?.height ?? 60

        // Update bounds considering node dimensions
        // minX/minY are the top-left corner, maxX/maxY are bottom-right corner
        minX = Math.min(minX, x)
        maxX = Math.max(maxX, x + width)
        minY = Math.min(minY, y)
        maxY = Math.max(maxY, y + height)
    }

    // Calculate content dimensions with padding
    // Add padding on all sides to give breathing room around the workflow
    const contentWidth = maxX - minX + padding * 2
    const contentHeight = maxY - minY + padding * 2

    // Clamp to minimum viewport size to ensure usability on small screens
    // This prevents the viewport from being too small when workflow is compact
    const width = Math.max(contentWidth, LAYOUT_CONSTANTS.MIN_VIEWPORT_WIDTH)
    const height = Math.max(contentHeight, LAYOUT_CONSTANTS.MIN_VIEWPORT_HEIGHT)

    // Add padding around the content for final bounds
    // These bounds are used by React Flow for scrolling and centering
    return {
        width,
        height,
        minX: minX - padding,
        maxX: maxX + padding,
        minY: minY - padding,
        maxY: maxY + padding,
    }
}

